import { rootResourceAtom } from 'atoms/root';
import routes from '~/routes';
import {
  HeaderMenu,
  HeaderMenuItem,
  Header as CarbonHeader,
  SkipToContent,
  HeaderMenuButton,
  HeaderName,
  HeaderNavigation,
  SideNav,
  SideNavItems,
  SideNavMenu,
  SideNavMenuItem,
} from '@carbon/react';
import { FeedbackDocumentation } from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Link } from 'react-router-dom';
import { ApiRootResponse } from 'types/api-responses';
import { useOpenReportForm } from 'components/report-form';
import { NotificationBell } from 'components/notification-feed';
import { logoutAtom, showSideNavAtom } from './atoms';
import style from './header.module.scss';
import StrongholdSvg from 'images/stronghold-logo-inverted.svg';

export interface HeaderProps {
  title: string;
}

const getSearchItems = (resources: ApiRootResponse['resources']) => {
  if ('sessions' in resources) {
    return [];
  }

  return [
    {
      title: 'Documents',
      href: resources['documents'].href,
    },
    {
      title: 'Map Query',
      href: routes.mapQueryPath,
    },
    {
      title: 'Search by Location',
      href: resources['referenceLocations'].href,
    },
  ] as const;
};

const getEvalItems = (resources: ApiRootResponse['resources']) => {
  if ('sessions' in resources) {
    return [];
  }

  return [
    {
      title: 'Packages',
      href: resources['packages'].href,
    },
    {
      title: 'Development Areas',
      href: resources['developmentAreas'].href,
    },
    {
      title: 'Expense Scenarios',
      href: routes.expenseScenariosPath,
    },
    {
      title: 'Scenarios',
      href: resources['scenarios'].href,
    },
    {
      title: 'Forward Price Curve',
      href: routes.forwardPriceCurvePath,
    },
    {
      title: 'Price Visualization',
      href: routes.pricingMarketDataPath,
    },
    {
      title: 'Price Decks',
      href: routes.priceDecksPath,
    },
    {
      title: 'Development Pace Scenarios',
      href: routes.developmentPaceScenariosPath,
    },
    {
      title: 'Discount Rate Scenarios',
      href: routes.discountRateScenariosPath,
    },
    {
      title: 'Oil & Gas Differentials',
      href: routes.differentialScenariosPath,
    },
    {
      title: 'Tax Scenarios',
      href: routes.taxScenariosPath,
    },
    {
      title: 'Evaluation Cases',
      href: routes.evaluationCasesPath,
    },
  ] as const;
};

const getTitleItems = (resources: ApiRootResponse['resources']) => {
  if ('sessions' in resources) {
    return [];
  }

  return [
    {
      title: 'Title Workspaces',
      href: resources['titleWorkspaces'].href,
    },
    {
      title: 'Title Support Workspaces',
      href: routes.titleSupportWorkspacesPath,
    },
    {
      title: 'Global Interpretations',
      href: resources['interpretations'].href,
    },
    {
      title: 'Entities',
      href: resources['entities'].href,
    },
  ] as const;
};

const getManageMenuItems = (resources: ApiRootResponse['resources']) => {
  if ('sessions' in resources) {
    return [];
  }

  return [
    {
      title: 'AFE Catalog',
      href: '/afe_catalog',
    },
    {
      title: 'Areas',
      href: resources['areaGeometries'].href,
    },
    {
      title: 'County Coverage',
      href: '/county_coverage',
    },
    {
      title: 'CRM',
      href: resources['crmTractsAndSubtracts'].href,
    },
    {
      title: 'DCA Scenarios',
      href: '/dca_scenarios',
    },
    {
      title: 'Division Orders',
      href: resources['divisionOrders'].href,
    },
    {
      title: 'Formation Management',
      href: resources['formationManagement'].href,
    },
    {
      title: 'Lexicon',
      href: routes.insightsLexiconPath,
    },
    {
      title: 'Royalty Checks',
      href: '/royalty_checks',
    },
    {
      title: 'State Taxes',
      href: resources['stateTaxes'].href,
    },
    {
      title: 'Structure Grids',
      href: resources['structureGrids'].href,
    },
    {
      title: 'Type Curve Formations',
      href: resources['typeCurveFormations'].href,
    },
    {
      title: 'Type Curves',
      href: '/type_curves',
    },
  ] as const;
};

// Pricing sub-group under Data Management, rendered as a cascading "Pricing" submenu.
const getPricingItems = (resources: ApiRootResponse['resources']) => {
  if ('sessions' in resources) {
    return [];
  }

  return [
    {
      title: 'Curve Management',
      href: routes.pricingCurveManagementPath,
    },
    {
      title: 'Pricing Contracts',
      href: routes.pricingContractBuilderPath,
    },
    {
      title: 'Price Transformations',
      href: routes.pricingTransformationBuilderPath,
    },
  ] as const;
};

const Header = ({ title }: HeaderProps) => {
  const rootResource = useAtomValue(rootResourceAtom);
  const [showSideNav, setShowSideNav] = useAtom(showSideNavAtom);
  const logout = useSetAtom(logoutAtom);
  // The caller states the intent, so the form opens on the right kind and the radio is a
  // correction rather than a required step.
  const openReportForm = useOpenReportForm();

  if (!rootResource) return null;

  const navSearchItems = getSearchItems(rootResource.resources);
  const navEvalItems = getEvalItems(rootResource.resources);
  const navTitleItems = getTitleItems(rootResource.resources);
  const navManageItems = getManageMenuItems(rootResource.resources);
  const navPricingItems = getPricingItems(rootResource.resources);

  const closeMenus = () => {
    setShowSideNav(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const menuSearchItems =
    navSearchItems?.map((navItem) => (
      <HeaderMenuItem<typeof Link>
        as={Link}
        to={navItem.href}
        key={navItem.href}
        onClick={closeMenus}
      >
        {navItem.title}
      </HeaderMenuItem>
    )) ?? null;

  const menuEvalItems =
    navEvalItems?.map((navEvalItem) => (
      <HeaderMenuItem<typeof Link>
        as={Link}
        to={navEvalItem.href}
        key={navEvalItem.href}
        onClick={closeMenus}
      >
        {navEvalItem.title}
      </HeaderMenuItem>
    )) ?? null;

  const menuManageItems =
    navManageItems?.map((navManageItem) => (
      <HeaderMenuItem<typeof Link>
        as={Link}
        to={navManageItem.href}
        key={navManageItem.href}
        onClick={closeMenus}
      >
        {navManageItem.title}
      </HeaderMenuItem>
    )) ?? null;

  const menuTitleItems =
    navTitleItems?.map((navTitleItem) => (
      <HeaderMenuItem<typeof Link>
        as={Link}
        to={navTitleItem.href}
        key={navTitleItem.href}
        onClick={closeMenus}
      >
        {navTitleItem.title}
      </HeaderMenuItem>
    )) ?? null;

  const menuPricingItems =
    navPricingItems?.map((navPricingItem) => (
      <HeaderMenuItem<typeof Link>
        as={Link}
        to={navPricingItem.href}
        key={navPricingItem.href}
        onClick={closeMenus}
      >
        {navPricingItem.title}
      </HeaderMenuItem>
    )) ?? null;

  const fullName = rootResource.session?.profile?.name;
  const initials = fullName
    ?.trim()
    .split(' ')
    .map((name) => name[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <CarbonHeader
      aria-label={`Stronghold ${title}`}
      className={style.insightsNavbar}
    >
      <SkipToContent />
      <HeaderMenuButton
        aria-label="Open menu"
        onClick={() => setShowSideNav(!showSideNav)}
        isActive={showSideNav}
      />
      <HeaderName<typeof Link> as={Link} to="/" prefix="">
        <img
          src={StrongholdSvg}
          className={style.strongholdLogo}
          alt="Stronghold"
        />
        {title}
      </HeaderName>
      <SideNav
        aria-label="Side navigation"
        expanded={showSideNav}
        isPersistent={false}
      >
        {rootResource.session && (
          <SideNavItems className={style.mobileLinks}>
            <SideNavMenu title="Search">{menuSearchItems}</SideNavMenu>
            <SideNavMenu title="Evaluation">{menuEvalItems}</SideNavMenu>
            <SideNavMenu title="Title">{menuTitleItems}</SideNavMenu>
            <SideNavMenu title="Data Management">
              {menuManageItems}
              <SideNavMenu title="Pricing">{menuPricingItems}</SideNavMenu>
            </SideNavMenu>
            {/* @ts-expect-error Carbon v11 SideNavMenuItem polymorphic typing */}
            <SideNavMenuItem
              as={Link}
              to="/admin/dashboard"
              onClick={() => setShowSideNav(false)}
            >
              Admin
            </SideNavMenuItem>
            <SideNavMenuItem
              as={Link}
              to={routes.bugsFeaturesPath}
              onClick={() => setShowSideNav(false)}
            >
              Feedback
            </SideNavMenuItem>
            <SideNavMenuItem
              href="#"
              aria-label="Log Out"
              onClick={() => logout()}
            >
              Log Out
            </SideNavMenuItem>
          </SideNavItems>
        )}
      </SideNav>
      {rootResource.session && (
        <HeaderNavigation aria-label="navbar">
          <HeaderMenu aria-label="Search" menuLinkName="Search">
            {menuSearchItems}
          </HeaderMenu>
          <HeaderMenu
            aria-label="Evaluation"
            menuLinkName="Evaluation"
            className={style.evaluationMenu}
          >
            {menuEvalItems}
          </HeaderMenu>
          <HeaderMenu aria-label="Title" menuLinkName="Title">
            {menuTitleItems}
          </HeaderMenu>
          <HeaderMenu
            aria-label="Data Management"
            menuLinkName="Data Management"
          >
            {menuManageItems}
            <HeaderMenu
              aria-label="Pricing"
              menuLinkName="Pricing"
              className={style.cascadeMenu}
            >
              {menuPricingItems}
            </HeaderMenu>
          </HeaderMenu>
          <HeaderMenuItem<typeof Link> as={Link} to="/admin/dashboard">
            Admin
          </HeaderMenuItem>
          {/*
            The listing, reachable by name rather than only by remembering the URL. Distinct
            from the icon button on the right, which opens the form to *file* something —
            this goes to what has already been filed.
          */}
          <HeaderMenuItem<typeof Link> as={Link} to={routes.bugsFeaturesPath}>
            Feedback
          </HeaderMenuItem>
        </HeaderNavigation>
      )}
      {rootResource.session ? (
        <div className={style.userNameArea}>
          {/*
            Sits inside userNameArea rather than the nav so it stays pinned right
            next to the user block on every breakpoint, and is not swallowed by
            the hamburger menu at small widths — a bug report is worth least when
            it is hard to find at the moment something breaks.
          */}
          {/*
            One entry point rather than a bug icon and a lightbulb. Carbon's
            FeedbackDocumentation reads as "tell us something" for both intents, and the
            form's own radio is where the choice belongs — two header icons implied a
            bigger distinction than the user has to make before they start typing.
          */}
          <button
            type="button"
            className={style.reportIssueButton}
            onClick={() => openReportForm('bug')}
            aria-label="Report a bug or request a feature"
            title="Report a bug or request a feature"
          >
            <FeedbackDocumentation size={16} />
          </button>
          {/*
            Beside Report an issue: the two are a pair — one files something, the other is
            how you hear back about it.
          */}
          <NotificationBell />
          <div className={style.userdata}>
            {rootResource.session.profile ? (
              <p className={style.userName}>
                {rootResource.session.profile?.name}
              </p>
            ) : null}
            <span
              className={style.logOut}
              aria-label="Log Out"
              onClick={() => logout()}
            >
              Log Out
            </span>
          </div>
          <div className={style.nameInitials}>{initials}</div>
        </div>
      ) : null}
    </CarbonHeader>
  );
};

export { Header };
